import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import ErrorToast from '../components/ErrorToast';
import { changePassword } from '../services/user-service';

const ChangePasswordPage: React.FC = () => {
    const navigate = useNavigate();

    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [errorToast, setErrorToast] = useState('');

    const validate = (): boolean => {
        const e: Record<string, string> = {};

        if (!oldPassword) e.oldPassword = 'Current password is required';

        if (!newPassword) {
            e.newPassword = 'New password is required';
        } else if (newPassword.length < 8 || newPassword.length > 15) {
            e.newPassword = 'Password must be between 8 and 15 characters long';
        } else if (!/[a-z]/.test(newPassword)) {
            e.newPassword = 'Password must include at least one lowercase letter';
        } else if (!/[A-Z]/.test(newPassword)) {
            e.newPassword = 'Password must include at least one uppercase letter';
        } else if (!/[0-9]/.test(newPassword)) {
            e.newPassword = 'Password must include at least one number';
        } else if (!/[^a-zA-Z0-9]/.test(newPassword)) {
            e.newPassword = 'Password must include at least one special character';
        }

        if (newPassword === oldPassword && newPassword.length > 0) {
            e.newPassword = 'New password must be different from current password';
        }

        if (confirmPassword !== newPassword) {
            e.confirmPassword = 'Passwords do not match';
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (ev: React.FormEvent) => {
        ev.preventDefault();
        setErrorToast('');

        if (!validate()) return;

        setLoading(true);
        try {
            const response = await changePassword({ oldPassword, newPassword });
            if (response?.status === 204) {
                navigate('/settings/profile', {
                    state: { message: 'Your password was updated successfully.' },
                });
            } else {
                setErrorToast('Unable to change password. Please check your current password and try again.');
            }
        } catch (err: any) {
            console.error(err);
            setErrorToast('Unable to change password. Please check your current password and try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <NavBar />
            <main className="flex-grow container mx-auto px-4 py-6">
                <button
                    data-testid="back-btn"
                    onClick={() => navigate('/settings/profile')}
                    className="text-crumbblue hover:underline mb-4"
                >
                    &larr; Back to Settings
                </button>

                <div className="max-w-md mx-auto p-6 bg-white rounded shadow">
                    <h2 className="text-center text-2xl font-semibold text-crumbblue">Change Password</h2>

                    <form onSubmit={handleSubmit} className="mt-6 space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Current password</label>
                            <input
                                type="password"
                                required
                                value={oldPassword}
                                onChange={(e) => setOldPassword(e.target.value)}
                                className="mt-1 w-full rounded-md border border-gray-300 p-2 focus:border-crumbblue focus:outline-none"
                                data-testid="old-password"
                            />
                            {errors.oldPassword && (
                                <div className="text-red-600 text-sm mt-1" data-testid="old-password-error">
                                    {errors.oldPassword}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">New password</label>
                            <input
                                type="password"
                                required
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="mt-1 w-full rounded-md border border-gray-300 p-2 focus:border-crumbblue focus:outline-none"
                                data-testid="new-password"
                            />
                            {errors.newPassword && (
                                <div className="text-red-600 text-sm mt-1" data-testid="new-password-error">
                                    {errors.newPassword}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Confirm new password</label>
                            <input
                                type="password"
                                required
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="mt-1 w-full rounded-md border border-gray-300 p-2 focus:border-crumbblue focus:outline-none"
                                data-testid="confirm-password"
                            />
                            {errors.confirmPassword && (
                                <div className="text-red-600 text-sm mt-1" data-testid="confirm-password-error">
                                    {errors.confirmPassword}
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full rounded-md bg-crumbblue p-2 font-semibold text-white hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                            data-testid="submit-btn"
                        >
                            {loading ? 'Updating...' : 'Update password'}
                        </button>
                    </form>

                    <ErrorToast message={errorToast} clearError={() => setErrorToast('')} />
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default ChangePasswordPage;